import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import { useCRM } from '../context/CRMContext';
import BaseModal from '../components/modals/BaseModal';
import SendContractModal from '../components/modals/SendContractModal';

const ContractTemplates = () => {
  const { contractTemplates, addTemplate, updateTemplate, deleteTemplate } = useCRM();
  
  const [viewMode, setViewMode] = useState('list');
  const [activeTemplate, setActiveTemplate] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('Alle');
  
  // Modals
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [useTemplateId, setUseTemplateId] = useState(null);

  // States for Create/Import
  const [newDocData, setNewDocData] = useState({ name: '', type: 'Tilbud', recipient: '' });
  const [importFile, setImportFile] = useState(null);
  const [importName, setImportName] = useState('');
  const [importType, setImportType] = useState('Kontrakt');

  const handleCreateStart = () => {
    setNewDocData({ name: '', type: 'Tilbud', recipient: '' });
    setIsCreateModalOpen(true);
  };

  const handleCreateConfirm = (e) => {
    e.preventDefault();
    const newTemplate = {
      name: newDocData.name,
      type: newDocData.type,
      recipient_placeholder: newDocData.recipient,
      content: '',
      status: 'draft',
      currency: 'NOK',
      isLocked: false,
      created_at: new Date().toISOString(),
      meta: {}
    };
    
    addTemplate(newTemplate);
    setActiveTemplate({ ...newTemplate, id: 'temp-new' });
    setViewMode('editor');
    setIsCreateModalOpen(false);
  };

  const handleImportSubmit = (e) => {
    e.preventDefault();
    if (!importFile) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const content = event.target.result;
      let newTemplate = {
        name: importName || importFile.name,
        type: importType,
        content: content,
        status: 'draft',
        currency: 'NOK',
        isLocked: false,
        created_at: new Date().toISOString(),
        meta: {}
      };

      // Prøv å parse som JSON hvis brukeren laster opp en backup-fil
      if (importFile.name.endsWith('.json')) {
        try {
          const parsed = JSON.parse(content);
          newTemplate = { ...newTemplate, ...parsed, id: undefined, name: importName || parsed.name || newTemplate.name };
        } catch (err) {
          console.warn("Kunne ikke parse JSON. Bruker som ren tekst.");
        }
      }

      addTemplate(newTemplate);
      setActiveTemplate({ ...newTemplate, id: 'temp-new' });
      setViewMode('editor');
      
      // Reset state
      setIsImportModalOpen(false);
      setImportFile(null);
      setImportName('');
    };
    reader.readAsText(importFile);
  };

  const handleEdit = (template) => {
    if (template.isLocked) return;
    setActiveTemplate(template);
    setViewMode('editor');
  };

  const handleDelete = (id) => {
    if (window.confirm('Er du sikker på at du vil slette denne malen?')) {
      deleteTemplate(id);
    }
  };

  const handleDuplicate = (template) => {
    const newTemplate = {
      ...template,
      id: undefined,
      name: template.name + ' (Kopi)',
      created_at: new Date().toISOString(),
      isLocked: false
    };
    addTemplate(newTemplate);
  };

  const handleToggleLock = (template) => {
    updateTemplate(template.id, { isLocked: !template.isLocked });
  };

  if (viewMode === 'editor') {
    return (
      <TemplateEditor 
        template={activeTemplate} 
        onSave={(updatedTmpl) => updateTemplate(updatedTmpl.id, updatedTmpl)}
        onSaveNew={(tmpl) => addTemplate(tmpl)}
        onBack={() => setViewMode('list')} 
      />
    );
  }

  return (
    <div className="p-8 space-y-8 max-w-7xl mx-auto min-h-screen">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
        <div>
          <h1 className="text-3xl font-black text-slate-900 tracking-tight">Dokumentmaler</h1>
          <p className="text-slate-500 font-medium mt-1">Opprett, importer og administrer kontrakter og tilbud.</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative hidden md:block group">
            <SafeIcon icon="🔍" className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-primary-500 transition-colors" />
            <input 
              type="text" 
              placeholder="Søk i maler..." 
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 pr-4 py-2.5 bg-white border border-slate-200 rounded-xl focus:border-primary-500 focus:ring-4 focus:ring-primary-500/10 outline-none transition-all w-64 font-bold text-sm"
            />
          </div>
          <button onClick={() => setIsImportModalOpen(true)} className="flex items-center justify-center gap-2 px-6 py-2.5 bg-white border border-slate-200 text-slate-700 font-black text-sm rounded-xl hover:bg-slate-50 shadow-sm transition-all active:scale-95">
            <SafeIcon icon="📥" className="w-4 h-4" /> Importer
          </button>
          <button onClick={handleCreateStart} className="flex items-center justify-center gap-2 px-6 py-2.5 bg-primary-600 text-white font-black text-sm rounded-xl hover:bg-primary-700 shadow-lg shadow-primary-200 transition-all active:scale-95">
            <SafeIcon icon="➕" className="w-4 h-4" /> Ny mal
          </button>
        </div>
      </div>

      <div className="flex gap-2 overflow-x-auto pb-2 no-scrollbar">
        {['Alle', 'Tilbud', 'Kontrakt', 'Avtale', 'Annet'].map(type => (
          <button 
            key={type}
            onClick={() => setFilterType(type)}
            className={`px-4 py-2 rounded-lg text-xs font-black uppercase tracking-widest whitespace-nowrap transition-all border ${filterType === type ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-500 border-slate-200 hover:border-slate-300'}`}
          >
            {type}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        <button onClick={handleCreateStart} className="group flex flex-col items-center justify-center min-h-[280px] bg-slate-50 border-2 border-dashed border-slate-300 rounded-[2rem] hover:border-primary-500 hover:bg-primary-50/30 transition-all cursor-pointer">
          <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center shadow-sm mb-4 group-hover:scale-110 transition-transform">
            <SafeIcon icon="➕" className="w-8 h-8 text-slate-400 group-hover:text-primary-600" />
          </div>
          <span className="font-black text-slate-900">Opprett ny mal</span>
          <span className="text-xs text-slate-500 font-bold mt-1">Start fra bunnen av</span>
        </button>

        <button onClick={() => setIsImportModalOpen(true)} className="group flex flex-col items-center justify-center min-h-[280px] bg-slate-50 border-2 border-dashed border-slate-300 rounded-[2rem] hover:border-blue-500 hover:bg-blue-50/30 transition-all cursor-pointer">
          <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center shadow-sm mb-4 group-hover:scale-110 transition-transform">
            <SafeIcon icon="📥" className="w-8 h-8 text-slate-400 group-hover:text-blue-600" />
          </div>
          <span className="font-black text-slate-900">Importer mal</span>
          <span className="text-xs text-slate-500 font-bold mt-1">Last opp .txt eller .json</span>
        </button>

        {contractTemplates
          .filter(t => filterType === 'Alle' || t.type === filterType)
          .filter(t => t.name.toLowerCase().includes(searchQuery.toLowerCase()))
          .map((template) => (
          <motion.div 
            key={template.id} 
            layout 
            className={`bg-white border-2 rounded-[2rem] overflow-hidden hover:shadow-xl hover:shadow-slate-200/50 transition-all group flex flex-col ${template.isLocked ? 'border-slate-200 bg-slate-50/30' : 'border-slate-200'}`}
          >
            <div className="h-32 bg-slate-100 relative overflow-hidden group-hover:opacity-90 transition-opacity">
              {template.meta?.coverSettings?.enabled && template.meta?.coverSettings?.imageUrl ? (
                <img src={template.meta.coverSettings.imageUrl} className="w-full h-full object-cover" alt="Cover" />
              ) : (
                <div className="absolute inset-0 flex items-center justify-center">
                  <SafeIcon icon="📄" className="w-12 h-12 text-slate-300" />
                </div>
              )}
              
              <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm px-2 py-1 rounded-md text-[10px] font-black uppercase tracking-widest text-slate-500 border border-slate-200/50 shadow-sm">
                {template.type || 'Kontrakt'}
              </div>
              {template.isLocked && (
                <div className="absolute top-4 left-4 bg-amber-100 text-amber-700 px-2 py-1 rounded-md text-[10px] font-black uppercase tracking-widest border border-amber-200 shadow-sm flex items-center gap-1">
                  <SafeIcon icon="🔒" className="w-3 h-3" /> Låst
                </div>
              )}
            </div>
            
            <div className="p-5 flex-1 flex flex-col">
              <h3 className="font-black text-slate-900 text-lg leading-tight group-hover:text-primary-600 transition-colors mb-2">{template.name}</h3>
              <p className="text-xs text-slate-500 font-medium mb-4 line-clamp-2">
                {template.meta?.coverSettings?.subtitle || (template.content ? template.content.substring(0, 60) + '...' : 'Ingen tekst tilgjengelig.')}
              </p>
              
              <div className="mt-auto pt-4 border-t border-slate-100 flex items-center justify-between">
                <span className="text-[10px] font-bold text-slate-400">
                  {new Date(template.created_at).toLocaleDateString()}
                </span>
                
                {/* Action Buttons */}
                <div className="flex flex-wrap justify-end gap-1">
                  <button onClick={() => setPreviewTemplate(template)} title="Vis" className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"><SafeIcon icon="👁️" className="w-4 h-4" /></button>
                  <button onClick={() => setUseTemplateId(template.id)} title="Bruk mal" className="p-1.5 text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"><SafeIcon icon="🚀" className="w-4 h-4" /></button>
                  <button onClick={() => handleDuplicate(template)} title="Dupliser" className="p-1.5 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"><SafeIcon icon="📋" className="w-4 h-4" /></button>
                  <button onClick={() => handleToggleLock(template)} title={template.isLocked ? "Lås opp" : "Lås (Skrivebeskytt)"} className="p-1.5 text-slate-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors">
                    <SafeIcon icon={template.isLocked ? "🔓" : "🔒"} className="w-4 h-4" />
                  </button>
                  
                  {!template.isLocked && (
                    <>
                      <button onClick={() => handleEdit(template)} title="Rediger" className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"><SafeIcon icon="✏️" className="w-4 h-4" /></button>
                      <button onClick={() => handleDelete(template.id)} title="Slett" className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"><SafeIcon icon="🗑️" className="w-4 h-4" /></button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* --- MODALS --- */}
      
      {/* Create New Document Modal */}
      <BaseModal isOpen={isCreateModalOpen} onClose={() => setIsCreateModalOpen(false)} title="Opprett nytt dokument" size="lg">
        <div className="p-1">
          <div className="flex items-center justify-between mb-8 bg-blue-50/50 p-4 rounded-xl border border-blue-100">
            <div className="flex gap-3">
              <SafeIcon icon="ℹ️" className="w-5 h-5 text-blue-500 mt-0.5" />
              <div>
                <h4 className="font-bold text-blue-900 text-sm">Hvordan komme i gang?</h4>
                <p className="text-xs text-blue-700 mt-1">Velg dokumenttype og gi det et navn for å starte redigeringen.</p>
              </div>
            </div>
          </div>
          <form onSubmit={handleCreateConfirm} className="space-y-6">
            <div className="space-y-3">
              <label className="text-xs font-black text-slate-500 uppercase tracking-widest"><span className="text-red-500 mr-1">*</span>Velg dokumenttype</label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {['Tilbud', 'Kontrakt', 'Avtale', 'Estimat', 'Rapport', 'Annet'].map((type) => (
                  <button 
                    key={type} 
                    type="button" 
                    onClick={() => setNewDocData({...newDocData, type})}
                    className={`px-4 py-3 rounded-xl border-2 text-sm font-bold transition-all ${newDocData.type === type ? 'border-primary-600 bg-primary-50 text-primary-700' : 'border-slate-100 text-slate-600 hover:border-slate-300'}`}
                  >{type}</button>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-xs font-black text-slate-500 uppercase tracking-widest"><span className="text-red-500 mr-1">*</span>Gi dokumentet et navn</label>
              <input 
                required 
                type="text" 
                placeholder="F.eks. Konsulentavtale 2024" 
                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:border-primary-500 outline-none font-bold transition-all"
                value={newDocData.name}
                onChange={(e) => setNewDocData({...newDocData, name: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <label className="text-xs font-black text-slate-500 uppercase tracking-widest"><span className="text-red-500 mr-1">*</span>Hvem sender du til?</label>
              <input 
                type="text" 
                placeholder="Skriv navn på leads eller kunder..." 
                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:border-primary-500 outline-none font-bold transition-all"
                value={newDocData.recipient}
                onChange={(e) => setNewDocData({...newDocData, recipient: e.target.value})}
              />
            </div>
            <div className="pt-6 flex justify-end gap-3 border-t border-slate-100">
              <button type="button" onClick={() => setIsCreateModalOpen(false)} className="px-6 py-3 text-slate-500 font-black text-xs uppercase tracking-widest hover:text-slate-900 transition-all">Avbryt</button>
              <button type="submit" className="px-8 py-3 bg-primary-600 text-white font-black text-sm rounded-xl hover:bg-primary-700 shadow-xl shadow-primary-200 transition-all">Opprett</button>
            </div>
          </form>
        </div>
      </BaseModal>

      {/* Import Document Modal */}
      <BaseModal isOpen={isImportModalOpen} onClose={() => { setIsImportModalOpen(false); setImportFile(null); setImportName(''); }} title="Importer mal" size="md">
        <form onSubmit={handleImportSubmit} className="space-y-6">
          <div 
            onClick={() => document.getElementById('template-upload').click()}
            className="border-2 border-dashed border-slate-300 rounded-[2rem] p-10 text-center hover:border-primary-500 hover:bg-primary-50 transition-all cursor-pointer group"
          >
            <input 
              id="template-upload" 
              type="file" 
              className="hidden" 
              accept=".txt,.md,.json"
              onChange={(e) => {
                const file = e.target.files[0];
                if (file) {
                  setImportFile(file);
                  if (!importName) setImportName(file.name.replace(/\.[^/.]+$/, ""));
                }
              }} 
            />
            <div className="w-16 h-16 bg-white shadow-sm rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
              <SafeIcon icon="📥" className="w-8 h-8 text-slate-400 group-hover:text-primary-600" />
            </div>
            <p className="font-black text-slate-700">Klikk for å laste opp fil</p>
            <p className="text-xs font-bold text-slate-400 mt-2">Støtter .txt, .md og JSON-backups</p>
          </div>

          {importFile && (
            <div className="bg-emerald-50 border border-emerald-100 p-4 rounded-xl flex items-center gap-3">
              <SafeIcon icon="✅" className="text-emerald-500" />
              <span className="text-sm font-bold text-emerald-800">{importFile.name}</span>
            </div>
          )}

          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-xs font-black text-slate-500 uppercase tracking-widest">Navn på mal</label>
              <input required value={importName} onChange={e => setImportName(e.target.value)} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:border-primary-500 outline-none font-bold text-sm transition-all" placeholder="F.eks. Standard NDA" />
            </div>
            <div className="space-y-2">
              <label className="text-xs font-black text-slate-500 uppercase tracking-widest">Dokumenttype</label>
              <select value={importType} onChange={e => setImportType(e.target.value)} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:border-primary-500 outline-none font-bold text-sm transition-all">
                <option value="Tilbud">Tilbud</option>
                <option value="Kontrakt">Kontrakt</option>
                <option value="Avtale">Avtale</option>
                <option value="Annet">Annet</option>
              </select>
            </div>
          </div>

          <div className="pt-6 flex justify-end gap-3 border-t border-slate-100">
            <button type="button" onClick={() => setIsImportModalOpen(false)} className="px-6 py-3 text-slate-500 font-black text-xs uppercase tracking-widest hover:text-slate-900 transition-all">Avbryt</button>
            <button type="submit" disabled={!importFile} className="px-8 py-3 bg-primary-600 text-white font-black text-sm rounded-xl hover:bg-primary-700 shadow-xl shadow-primary-200 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2">
              <SafeIcon icon="📥" /> Importer
            </button>
          </div>
        </form>
      </BaseModal>

      {/* Preview Modal */}
      <BaseModal isOpen={!!previewTemplate} onClose={() => setPreviewTemplate(null)} title={`Forhåndsvisning: ${previewTemplate?.name}`} size="2xl">
        {previewTemplate && (
          <div className="max-h-[70vh] overflow-y-auto bg-slate-100 p-8 rounded-xl flex flex-col gap-8 items-center custom-scrollbar">
            {previewTemplate.meta?.coverSettings?.enabled && (
              <div className="w-full max-w-[800px] min-h-[800px] bg-white shadow-lg flex flex-col overflow-hidden">
                {previewTemplate.meta.coverSettings.imageUrl && (
                  <div className="w-full h-1/2 min-h-[400px] bg-slate-100">
                    <img src={previewTemplate.meta.coverSettings.imageUrl} className="w-full h-full object-cover" alt="Cover" />
                  </div>
                )}
                <div className="flex-1 flex flex-col justify-center items-center text-center p-12">
                  <h1 className="text-5xl font-black mb-6 text-slate-900">{previewTemplate.meta.coverSettings.title}</h1>
                  <p className="text-xl text-slate-500">{previewTemplate.meta.coverSettings.subtitle}</p>
                </div>
              </div>
            )}
            <div className="w-full max-w-[800px] min-h-[800px] bg-white shadow-lg p-16 whitespace-pre-wrap font-serif text-slate-800 leading-relaxed">
              {previewTemplate.content || 'Ingen tekst lagt til enda.'}
            </div>
          </div>
        )}
      </BaseModal>

      {/* Send Contract Modal */}
      <SendContractModal 
        isOpen={!!useTemplateId} 
        initialTemplateId={useTemplateId} 
        onClose={() => setUseTemplateId(null)} 
      />

    </div>
  );
};


// --- Sub-Component: Template Editor (Norsk) ---
const TemplateEditor = ({ template, onSave, onSaveNew, onBack }) => {
  const { products, customFieldDefinitions, addCustomFieldDef, deleteCustomFieldDef } = useCRM();
  
  const [content, setContent] = useState(template?.content || '');
  const [docName, setDocName] = useState(template?.name || '');
  const [activeTab, setActiveTab] = useState('settings');

  // Settings State
  const [docSettings, setDocSettings] = useState(template?.meta?.docSettings || {
    url: 'https://crm.bedrift.no/doc-' + Math.random().toString(36).substr(2, 9),
    number: 'TILBUD-2024-001',
    amount: '0,00 NOK',
    title: template?.name || '',
    openTill: '',
    createdAt: template?.created_at ? new Date(template.created_at).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
  });
  
  const [workspaceSettings, setWorkspaceSettings] = useState(template?.meta?.workspaceSettings || {
    ownerEmail: 'salg@bedrift.no'
  });
  
  const [customFields, setCustomFields] = useState(template?.meta?.customFields || {});
  
  const [layoutSettings, setLayoutSettings] = useState(template?.meta?.layoutSettings || {
    backgroundColor: '#ffffff',
    baseFont: 'Nunito',
    fontColor: '#323232'
  });

  // NY: Cover Settings
  const [coverSettings, setCoverSettings] = useState(template?.meta?.coverSettings || {
    enabled: false,
    imageUrl: '',
    title: template?.name || 'Dokumenttittel',
    subtitle: 'Kort beskrivelse av innholdet'
  });

  // Pricing Table Logic
  const [selectedItems, setSelectedItems] = useState(template?.meta?.pricing || []);
  
  const totalPrice = useMemo(() => {
    return selectedItems.reduce((sum, item) => sum + (item.price * (item.quantity || 1)), 0);
  }, [selectedItems]);

  const handleSave = () => {
    const updated = {
      ...template,
      name: docName,
      content,
      meta: { docSettings, workspaceSettings, customFields, layoutSettings, pricing: selectedItems, coverSettings },
      updated_at: new Date().toISOString()
    };
    
    if (template.id === 'temp-new') {
      onSaveNew({ ...updated, id: undefined });
      onBack();
    } else {
      onSave(updated);
    }
  };

  const insertText = (text) => setContent(prev => prev + text);

  const insertPricingTable = () => {
    if (selectedItems.length === 0) return alert('Vennligst velg produkter først i "Produkter" fanen.');
    
    let table = '\n\n| Produkt | Pris | Antall | Totalt |\n| :--- | :--- | :--- | :--- |\n';
    selectedItems.forEach(item => {
      table += `| ${item.name} | ${item.price} kr | ${item.quantity || 1} | ${(item.price * (item.quantity || 1))} kr |\n`;
    });
    table += `| **TOTALT** | | | **${totalPrice} kr** |\n\n`;
    
    insertText(table);
    setDocSettings(prev => ({ ...prev, amount: `${totalPrice} NOK` }));
  };

  const stylingBlocks = [
    { icon: "T", label: 'Tekst', action: () => insertText('\n\n[Tekstblokk]\n') },
    { icon: "🖼️", label: 'Bilde', action: () => insertText('\n\n[Bildeplassholder]\n') },
    { icon: "▦", label: 'Tabell', action: () => insertText('\n\n| Kol 1 | Kol 2 |\n|-------|-------|\n| Val 1 | Val 2 |\n') },
    { icon: "➖", label: 'Skillelinje', action: () => insertText('\n\n---\n\n') },
    { icon: "✂️", label: 'Sideskift', action: () => insertText('\n\n[Sideskift]\n\n') },
  ];

  const dynamicBlocks = [
    { icon: "🖊️", label: 'Signaturfelt', action: () => insertText('\n\n[Signaturfelt]\n') },
    { icon: "💰", label: 'Pristabell', action: insertPricingTable },
  ];

  const mergeFields = [
    {
      group: 'Kunde Info',
      fields: [
        { label: 'Navn', key: '[Receiver.Name]' },
        { label: 'Fornavn', key: '[Receiver.FirstName]' },
        { label: 'Etternavn', key: '[Receiver.LastName]' },
        { label: 'E-post', key: '[Receiver.Email]' },
        { label: 'Firma', key: '[Receiver.Company]' },
        { label: 'Adresse', key: '[Receiver.Address]' },
      ]
    },
    {
      group: 'Avsender Info',
      fields: [
        { label: 'Mitt Navn', key: '[Sender.Name]' },
        { label: 'Mitt Firma', key: '[Sender.Company]' },
      ]
    }
  ];

  return (
    <div className="fixed inset-0 z-50 bg-slate-50 flex flex-col font-sans">
      {/* Top Bar */}
      <div className="h-16 bg-slate-900 text-white flex items-center justify-between px-6 shadow-md shrink-0">
        <div className="flex items-center gap-4">
          <button onClick={onBack} className="p-2 hover:bg-white/10 rounded-lg transition-all"><SafeIcon icon="⬅️" className="w-5 h-5" /></button>
          <div className="flex items-center gap-2">
            <input 
              value={docName} 
              onChange={(e) => setDocName(e.target.value)} 
              className="bg-transparent border-none text-white font-black text-lg focus:ring-0 px-0 w-64" 
            />
            <span className="px-2 py-0.5 bg-white/10 rounded text-[10px] font-bold uppercase tracking-widest text-slate-300">Utkast</span>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={handleSave} className="flex items-center gap-2 px-4 py-2 bg-white/10 text-white text-xs font-bold rounded-lg hover:bg-white/20 transition-all"><SafeIcon icon="💾" className="w-4 h-4" /> Lagre</button>
          <button onClick={onBack} className="flex items-center gap-2 px-6 py-2 bg-primary-600 text-white text-xs font-bold rounded-lg hover:bg-primary-500 transition-all shadow-lg shadow-primary-900/50"><SafeIcon icon="✕" className="w-4 h-4" /> Lukk Editor</button>
        </div>
      </div>

      <div className="flex-1 flex overflow-hidden">
        {/* Left Sidebar: Blocks */}
        <div className="w-20 lg:w-72 bg-white border-r border-slate-200 flex flex-col shrink-0 overflow-y-auto custom-scrollbar">
          <div className="p-6 pb-2">
            <h3 className="font-black text-slate-900 text-sm uppercase tracking-widest">Innhold</h3>
            <p className="text-xs text-slate-500 mt-1">Sett markøren og klikk på en blokk.</p>
          </div>
          <div className="p-6 pt-2 space-y-6">
            <div>
              <h4 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-3">Designelementer</h4>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                {stylingBlocks.map((block, i) => (
                  <button key={i} onClick={block.action} className="flex flex-col items-center justify-center gap-2 p-4 bg-slate-50 border border-slate-200 rounded-lg hover:border-primary-500 hover:bg-primary-50 hover:text-primary-700 transition-all group text-slate-600 h-24">
                    <SafeIcon icon={block.icon} className="w-6 h-6 shrink-0" />
                    <span className="text-xs font-bold">{block.label}</span>
                  </button>
                ))}
              </div>
            </div>
            <div>
              <h4 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-3">Dynamiske Blokker</h4>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                {dynamicBlocks.map((block, i) => (
                  <button key={i} onClick={block.action} className="flex flex-col items-center justify-center gap-2 p-4 bg-amber-50 border border-amber-200 rounded-lg hover:border-amber-400 hover:bg-amber-100/50 transition-all group text-amber-900 h-24">
                    <SafeIcon icon={block.icon} className="w-5 h-5 shrink-0" />
                    <span className="text-xs font-bold">{block.label}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Center: Editor Canvas */}
        <div className="flex-1 bg-slate-100 overflow-y-auto p-8 flex flex-col items-center gap-8 custom-scrollbar">
          
          {/* Cover Page (Render if enabled) */}
          {coverSettings.enabled && (
             <div className="w-full max-w-[850px] min-h-[1100px] shadow-xl shadow-slate-200/50 rounded-sm relative flex flex-col overflow-hidden bg-white shrink-0">
               {coverSettings.imageUrl ? (
                 <div className="w-full h-1/2 min-h-[450px] bg-slate-100">
                    <img src={coverSettings.imageUrl} alt="Cover placeholder" className="w-full h-full object-cover" />
                 </div>
               ) : (
                 <div className="w-full h-1/3 min-h-[300px] bg-slate-50 flex items-center justify-center border-b border-slate-100">
                    <span className="text-slate-300 text-sm font-bold uppercase tracking-widest">Bildeplassholder (Legg til URL i innstillinger)</span>
                 </div>
               )}
               <div className="flex-1 flex flex-col justify-center p-16 text-center">
                 <h1 className="text-5xl font-black text-slate-900 mb-6">{coverSettings.title || 'Dokumenttittel'}</h1>
                 <p className="text-xl text-slate-500 font-medium">{coverSettings.subtitle || 'Dokumentundertittel'}</p>
               </div>
             </div>
          )}

          {/* Main Document Page */}
          <div className="w-full max-w-[850px] min-h-[1100px] shadow-xl shadow-slate-200/50 rounded-sm p-12 relative flex flex-col transition-all duration-300 shrink-0" style={{ backgroundColor: layoutSettings.backgroundColor, color: layoutSettings.fontColor }}>
            <div className="absolute top-0 left-0 right-0 h-10 border-b border-slate-200/50 flex items-center px-4 gap-2 text-slate-500 bg-white/50 backdrop-blur-sm z-10">
              <button className="p-1 hover:bg-slate-200 rounded"><SafeIcon icon="B" className="w-4 h-4" /></button>
              <button className="p-1 hover:bg-slate-200 rounded"><SafeIcon icon="I" className="w-4 h-4" /></button>
              <button className="p-1 hover:bg-slate-200 rounded"><SafeIcon icon="U" className="w-4 h-4" /></button>
              <div className="w-px h-4 bg-slate-300 mx-1"></div>
              <button className="p-1 hover:bg-slate-200 rounded"><SafeIcon icon="⇤" className="w-4 h-4" /></button>
              <button className="p-1 hover:bg-slate-200 rounded"><SafeIcon icon="↔" className="w-4 h-4" /></button>
              <button className="p-1 hover:bg-slate-200 rounded"><SafeIcon icon="⇥" className="w-4 h-4" /></button>
            </div>
            <div className="mt-8 flex-1 relative">
              <textarea 
                value={content} 
                onChange={(e) => setContent(e.target.value)} 
                placeholder="Start skrivingen her..." 
                className="w-full h-full resize-none outline-none text-lg leading-relaxed placeholder:text-slate-300 bg-transparent font-serif"
                style={{ color: 'inherit' }}
              />
            </div>
          </div>
        </div>

        {/* Right Sidebar */}
        <div className="w-80 bg-white border-l border-slate-200 flex flex-col shrink-0">
          <div className="flex border-b border-slate-100 overflow-x-auto no-scrollbar">
            <button onClick={() => setActiveTab('settings')} className={`flex-1 py-4 px-2 text-[10px] font-black uppercase tracking-widest min-w-[80px] ${activeTab === 'settings' ? 'text-primary-600 border-b-2 border-primary-600' : 'text-slate-400'}`}>
              <SafeIcon icon="⚙️" className="w-4 h-4 mx-auto mb-1" />
              Innstillinger
            </button>
            <button onClick={() => setActiveTab('products')} className={`flex-1 py-4 px-2 text-[10px] font-black uppercase tracking-widest min-w-[80px] ${activeTab === 'products' ? 'text-primary-600 border-b-2 border-primary-600' : 'text-slate-400'}`}>
              <SafeIcon icon="🛒" className="w-4 h-4 mx-auto mb-1" />
              Produkter
            </button>
            <button onClick={() => setActiveTab('merge')} className={`flex-1 py-4 px-2 text-[10px] font-black uppercase tracking-widest min-w-[80px] ${activeTab === 'merge' ? 'text-primary-600 border-b-2 border-primary-600' : 'text-slate-400'}`}>
              <SafeIcon icon="🗄️" className="w-4 h-4 mx-auto mb-1" />
              Flettefelt
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-6 custom-scrollbar">
            {activeTab === 'settings' && (
              <div className="space-y-8">
                
                {/* FORSIDE INNSTILLINGER (NY) */}
                <div className="space-y-4">
                  <h3 className="font-black text-slate-900 text-xs uppercase tracking-widest flex items-center justify-between pb-2 border-b border-slate-100">
                    <span className="flex items-center gap-2"><SafeIcon icon="🖼️" /> Forside</span>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input 
                        type="checkbox" 
                        className="sr-only peer" 
                        checked={coverSettings.enabled} 
                        onChange={e => setCoverSettings({...coverSettings, enabled: e.target.checked})} 
                      />
                      <div className="w-7 h-4 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-3 after:w-3 after:transition-all peer-checked:bg-primary-600"></div>
                    </label>
                  </h3>
                  
                  {coverSettings.enabled && (
                    <div className="space-y-3 animate-in fade-in slide-in-from-top-2">
                      <div className="space-y-1">
                        <label className="text-[10px] font-bold text-slate-500">Forsidebilde URL</label>
                        <input 
                          value={coverSettings.imageUrl} 
                          onChange={e => setCoverSettings({...coverSettings, imageUrl: e.target.value})} 
                          placeholder="https://..." 
                          className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-md text-xs text-slate-700 outline-none focus:border-primary-500" 
                        />
                      </div>
                      <div className="space-y-1">
                        <label className="text-[10px] font-bold text-slate-500">Hovedtittel</label>
                        <input 
                          value={coverSettings.title} 
                          onChange={e => setCoverSettings({...coverSettings, title: e.target.value})} 
                          className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-md text-sm font-bold text-slate-700 outline-none focus:border-primary-500" 
                        />
                      </div>
                      <div className="space-y-1">
                        <label className="text-[10px] font-bold text-slate-500">Undertittel</label>
                        <textarea 
                          rows="2"
                          value={coverSettings.subtitle} 
                          onChange={e => setCoverSettings({...coverSettings, subtitle: e.target.value})} 
                          className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-md text-sm text-slate-700 outline-none focus:border-primary-500 resize-none" 
                        />
                      </div>
                    </div>
                  )}
                </div>

                <div className="space-y-4 pt-4 border-t border-slate-100">
                  <h3 className="font-black text-slate-900 text-xs uppercase tracking-widest pb-2 border-b border-slate-100">Dokumentinfo</h3>
                  <div className="space-y-1">
                    <label className="text-[10px] font-bold text-slate-500">Dokument Lenke</label>
                    <input value={docSettings.url} readOnly className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-md text-xs text-slate-400" />
                  </div>
                  <div className="space-y-1">
                    <label className="text-[10px] font-bold text-slate-500">Dokumentnummer</label>
                    <input value={docSettings.number} onChange={(e) => setDocSettings({...docSettings, number: e.target.value})} className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-md text-sm text-slate-700 focus:border-primary-500 outline-none" />
                  </div>
                  <div className="space-y-1">
                    <label className="text-[10px] font-bold text-slate-500">Beløp</label>
                    <input value={docSettings.amount} onChange={(e) => setDocSettings({...docSettings, amount: e.target.value})} className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-md text-sm text-slate-700 focus:border-primary-500 outline-none" />
                  </div>
                </div>

                <div className="space-y-4 pt-4 border-t border-slate-100">
                  <div className="flex items-center justify-between pb-2 border-b border-slate-100">
                    <h3 className="font-black text-slate-900 text-xs uppercase tracking-widest">Egendefinerte felt</h3>
                    <button onClick={() => {
                      const label = prompt('Feltnavn:');
                      if (label) addCustomFieldDef({ label, key: `[Receiver.${label.replace(/\s/g, '')}]`, type: 'text' });
                    }} className="p-1 text-primary-600 hover:bg-primary-50 rounded transition-colors"><SafeIcon icon="➕" className="w-3 h-3" /></button>
                  </div>
                  {customFieldDefinitions.map(field => (
                    <div key={field.id} className="space-y-1 group/field">
                      <div className="flex justify-between items-center">
                        <label className="text-[10px] font-bold text-slate-500">{field.label} {field.key}</label>
                        <button onClick={() => deleteCustomFieldDef(field.id)} className="opacity-0 group-hover/field:opacity-100 p-1 text-red-500 hover:bg-red-50 rounded transition-all"><SafeIcon icon="🗑️" className="w-3 h-3" /></button>
                      </div>
                      <input 
                        value={customFields[field.key] || ''} 
                        onChange={(e) => setCustomFields({...customFields, [field.key]: e.target.value})} 
                        placeholder={`Skriv inn ${field.label}...`}
                        className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-md text-sm text-slate-700 focus:border-primary-500 outline-none" 
                      />
                    </div>
                  ))}
                </div>

                <div className="space-y-4 pt-4 border-t border-slate-100">
                  <h3 className="font-black text-slate-900 text-xs uppercase tracking-widest pb-2 border-b border-slate-100">Utseende (Brødtekst)</h3>
                  <div className="space-y-1">
                    <label className="text-[10px] font-bold text-slate-500">Bakgrunnsfarge</label>
                    <div className="flex items-center gap-2">
                      <input type="color" value={layoutSettings.backgroundColor} onChange={(e) => setLayoutSettings({...layoutSettings, backgroundColor: e.target.value})} className="h-9 w-9 p-0 border-0 rounded cursor-pointer" />
                      <input value={layoutSettings.backgroundColor} onChange={(e) => setLayoutSettings({...layoutSettings, backgroundColor: e.target.value})} className="flex-1 px-3 py-2 bg-white border border-slate-200 rounded-md text-sm font-mono text-slate-700 uppercase" />
                    </div>
                  </div>
                </div>

              </div>
            )}

            {activeTab === 'products' && (
              <div className="space-y-6">
                <div className="bg-amber-50 p-4 rounded-xl border border-amber-100">
                  <h4 className="font-bold text-amber-900 text-sm mb-1">Prisliste</h4>
                  <p className="text-xs text-amber-700">Velg produkter du ønsker å inkludere i pristabellen.</p>
                </div>
                <div className="space-y-3">
                  {products.map(product => {
                    const selected = selectedItems.find(i => i.id === product.id);
                    return (
                      <div 
                        key={product.id} 
                        className={`p-3 rounded-xl border-2 transition-all cursor-pointer ${selected ? 'border-primary-500 bg-primary-50' : 'border-slate-100 hover:border-slate-200'}`}
                        onClick={() => {
                          if (selected) {
                            setSelectedItems(prev => prev.filter(i => i.id !== product.id));
                          } else {
                            setSelectedItems(prev => [...prev, { ...product, quantity: 1 }]);
                          }
                        }}
                      >
                        <div className="flex justify-between items-start">
                          <h5 className="font-black text-slate-900 text-sm">{product.name}</h5>
                          {selected && <SafeIcon icon="✅" className="text-primary-600 w-4 h-4" />}
                        </div>
                        <p className="text-[10px] text-slate-500 mt-1">{product.description}</p>
                        
                        <div className="flex justify-between items-center mt-3">
                          <span className="font-black text-primary-600 font-mono text-sm">{product.price} kr</span>
                          {selected && (
                            <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-lg p-1" onClick={(e) => e.stopPropagation()}>
                              <button onClick={() => setSelectedItems(prev => prev.map(i => i.id === product.id ? { ...i, quantity: Math.max(1, i.quantity - 1) } : i))} className="p-1 hover:bg-slate-100 rounded text-slate-500"><SafeIcon icon="➖" className="w-3 h-3" /></button>
                              <span className="text-xs font-black w-4 text-center">{selected.quantity}</span>
                              <button onClick={() => setSelectedItems(prev => prev.map(i => i.id === product.id ? { ...i, quantity: i.quantity + 1 } : i))} className="p-1 hover:bg-slate-100 rounded text-slate-500"><SafeIcon icon="➕" className="w-3 h-3" /></button>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
                
                {selectedItems.length > 0 && (
                  <div className="pt-4 border-t border-slate-100">
                    <div className="flex justify-between items-center mb-4">
                      <span className="text-xs font-bold text-slate-500 uppercase">Valgt total:</span>
                      <span className="font-black text-slate-900">{totalPrice} kr</span>
                    </div>
                    <button onClick={insertPricingTable} className="w-full py-3 bg-primary-600 text-white font-black text-xs uppercase tracking-widest rounded-xl shadow-lg shadow-primary-200 hover:bg-primary-700 transition-all flex items-center justify-center gap-2">
                      <SafeIcon icon="➕" className="w-4 h-4" /> Sett inn tabell
                    </button>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'merge' && (
              <div className="space-y-6">
                <div className="bg-blue-50 p-4 rounded-xl border border-blue-100">
                  <h4 className="font-bold text-blue-900 text-sm mb-1">FLETTEFELT</h4>
                  <p className="text-xs text-blue-700">Klikk på et felt for å sette det inn i dokumentet.</p>
                </div>
                
                {mergeFields.map((group, idx) => (
                  <div key={idx} className="space-y-3">
                    <h4 className="font-black text-slate-900 text-sm uppercase tracking-widest text-[10px] text-slate-400">{group.group}</h4>
                    <div className="space-y-2">
                      {group.fields.map((field, fIdx) => (
                        <button key={fIdx} onClick={() => insertText(` ${field.key} `)} className="w-full text-left px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-xs hover:bg-primary-50 hover:border-primary-300 hover:text-primary-700 transition-all">
                          <span className="font-bold text-slate-700">{field.label}</span>
                          <span className="ml-2 text-slate-400 font-mono">{field.key}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContractTemplates;